/**
 * ProjectDetailView -- Vista detallada de un proyecto del ecosistema con pestanas.
 *
 * Ruta: #/projects/:key
 *
 * Pestanas:
 *   1. Canon         — instruccion rectora (relation='canon' del endpoint T2).
 *   2. Items         — items vinculados agrupados por tipo (endpoint T2).
 *   3. Misiones      — misiones vivas: por prefijo, por mencion o porque el dueno del repo las nombra.
 *   4. Bitacora      — lo del dueno del repo (por origen) y lo que nombra el proyecto.
 *   5. Instrucciones — todas las instrucciones del proyecto (subset de Items).
 *   6. Operacional   — version, branch, deploy, statusMetadata (read-only).
 *
 * Mision: PROJECT-ITEMS-HUB-001 / T3 (300 NXS).
 *
 * @module presentation/views/ProjectDetailView
 */

import { ProjectApiClient }       from '../../infrastructure/api/ProjectApiClient.js';
import { ProjectItemsApiClient }  from '../../infrastructure/api/ProjectItemsApiClient.js';
import { InstructionApiClient }   from '../../infrastructure/api/InstructionApiClient.js';
import { MissionApiClient }       from '../../infrastructure/api/MissionApiClient.js';
import { TaskApiClient }          from '../../infrastructure/api/TaskApiClient.js';
import { BitacoraApiClient }      from '../../infrastructure/api/BitacoraApiClient.js';
import { SaludApiClient }         from '../../infrastructure/api/SaludApiClient.js';
import { selloDe }                from '../../infrastructure/api/SelloVivoClient.js';
import { selloDeVerificacion, duenoDelNodo, cuandoLegible } from '../../domain/verificacionNodo.js';
import { agruparPorFamilia } from '../../application/proyectos/familiasEndpoints.js';
import { leerFila, duenoDelRepo, origenesDelDueno, cincoPreguntas, lineaRespaldo, misionesDeLosEventos }
  from '../../application/proyectos/saludDelProyecto.js';
import { cambiosRecientes, bloqueosAbiertos, proximosPasos, itemsAnotados, veredictoDe, listaParaGuardar, repoDe }
  from '../../application/proyectos/operacionalDelProyecto.js';

// ─── helpers ─────────────────────────────────────────────────────────────────

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

const STATUS_COLOR = {
  active: '#238636', operational: '#238636', production: '#238636',
  paused: '#d29922', beta: '#d29922', alpha: '#58a6ff',
  deprecated: '#f85149', maintenance: '#6e7681', por_cerrar: '#f0883e',
};

const RELATION_COLOR = {
  canon:   '#a371f7',
  hosts:   '#58a6ff',
  exposes: '#3fb950',
  uses:    '#8b949e',
};

const TYPE_LABEL = {
  instruction:    { label: 'Instrucciones',    icon: '📜' },
  form:           { label: 'Formularios',       icon: '📝' },
  endpoint:       { label: 'Endpoints',         icon: '🔌' },
  view:           { label: 'Vistas',            icon: '🖥️' },
  capability:     { label: 'Capabilities',      icon: '🧠' },
  workflow:       { label: 'Workflows',         icon: '⚡' },
  component:      { label: 'Componentes',       icon: '🧩' },
  infrastructure: { label: 'Infraestructura',   icon: '⚙️' },
  table:          { label: 'Tablas',            icon: '🗃️' },
};

const TYPE_ORDER = ['instruction', 'form', 'endpoint', 'view', 'capability', 'workflow', 'component', 'infrastructure', 'table'];

function badge(text, overrideColor) {
  const color = overrideColor || STATUS_COLOR[String(text || '').toLowerCase()] || '#6e7681';
  return `<span class="proj-badge" style="background:${color}22;color:${color}">${esc(text || '—')}</span>`;
}

function relBadge(rel) {
  const c = RELATION_COLOR[rel] || '#6e7681';
  return `<span class="proj-badge" style="background:${c}22;color:${c};font-size:0.875rem">${esc(rel)}</span>`;
}

function pctBar(pct) {
  const v = parseFloat(pct) || 0;
  const c = v >= 70 ? '#238636' : v >= 35 ? '#d29922' : '#6e7681';
  return `<div class="proj-progress-bar" title="${v.toFixed(1)}%"><div style="width:${v}%;background:${c}"></div></div>`;
}

function safeJsonParse(s) {
  if (!s) return {};
  if (typeof s === 'object') return s;
  try { return JSON.parse(s); } catch { return {}; }
}

function safeArr(v) {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch { return []; } }
  return [];
}

function getKeyFromHash() {
  const parts = window.location.hash.split('/');
  return parts[parts.length - 1] || '';
}

// ─── Cobertura de documentacion (ARGY-DOC-ARCH-AUDIT-001) ─────────────────────
// Fuente: statusMetadata.doc_coverage — Ares lo puebla con el mapeo de Argy-Q.
// Modelo v2 (metrica:'v2'): docs_md, docs_en_center, pct_documentado (=docs_en_center/docs_md),
// carpeta_principal_alineada (bool true=alineada | false | string="PR ..."=pendiente).
// Compat v1: docs_mapeados_center (alias de docs_en_center); carpetas_totales/archivos_totales
// opcionales (v2 no los trae -> se ocultan). Lectura defensiva de todos.
// Si el proyecto no tiene doc_coverage dice "sin auditoria" (PROYECTOS-ALINEADOS-T1, Ares #39216): antes pintaba una
// DEMOSTRACION de 2 %, un numero inventado que Josse leyo como el de su escritorio.
function docCoverageOf(project) {
  const meta = safeJsonParse(project?.statusMetadata);
  const dc = meta && (meta.doc_coverage || meta.docCoverage);
  if (dc && typeof dc === 'object') return { data: dc, sinAuditoria: false };
  return { data: null, sinAuditoria: true };
}

// La version escrita a mano en la ficha: chica, gris y con su fecha, nunca en lugar de la servida (Ares #39216).
function anotadaHtml(project) {
  if (!project.currentVersion) return '';
  const f = project.lastDeployAt || project.updatedAt;
  const cuando = f ? formatearFecha(f) : 'sin fecha';
  return `<div class="pdt-anotada" data-pdt-anotada>anotada a mano: v${esc(project.currentVersion)} (${esc(cuando)})</div>`;
}

// docs centralizados en Center (v2 docs_en_center; compat v1 docs_mapeados_center).
function docsEnCenter(dc) {
  return Number(dc.docs_en_center ?? dc.docs_mapeados_center ?? 0) || 0;
}

// pct efectivo: usa pct_documentado si viene; sino lo deriva (docs_en_center / docs_md).
function docCoveragePct(dc) {
  if (dc.pct_documentado != null && dc.pct_documentado !== '') return Number(dc.pct_documentado) || 0;
  const md = Number(dc.docs_md) || 0;
  return md > 0 ? (docsEnCenter(dc) / md) * 100 : 0;
}

// (b) docs pendientes por centralizar = docs_md - docs_en_center. null si faltan datos.
function docsPendientes(dc) {
  const md = Number(dc.docs_md);
  if (!Number.isFinite(md)) return null;
  return Math.max(0, md - docsEnCenter(dc));
}

// Estado de carpeta_principal_alineada: bool (Si/No) o string (=pendiente, chip amarillo con texto).
function alineadaState(v) {
  if (v === true)  return { kind: 'ok',      icon: '&#9989;',   label: 'Si',        color: '#238636', detail: '' };
  if (v === false) return { kind: 'no',      icon: '&#9940;',   label: 'No',        color: '#f85149', detail: '' };
  const s = String(v ?? '').trim();
  if (s)           return { kind: 'pending', icon: '&#128993;', label: 'Pendiente', color: '#d29922', detail: s };
  return { kind: 'unknown', icon: '', label: '—', color: '#8b949e', detail: '' };
}

// Badge del tab: % real (verde/amber) o '' cuando es demo (no engaña con un numero falso).
function docCoverageBadge(project) {
  const { data, sinAuditoria } = docCoverageOf(project);
  if (sinAuditoria) return '';
  return `${Math.round(docCoveragePct(data))}%`;
}

// ─── UX artefacto Proyectos (ARGY-DOC-ARCH-AUDIT-001 #2444) ───────────────────
// Estado de un item leido de metadata.status: 'live' (activo/sin status), 'planned'
// (futuro, aun no construido) o 'deprecated' (legacy retirado -> Archivados).
const ITEM_STATUS = {
  deprecated: { label: 'Archivado',   color: '#c9995a', icon: '🗄️' },
  planned:    { label: 'Planificado', color: '#a371f7', icon: '🔮' },
};
function itemStatusOf(item) {
  const s = String(safeJsonParse(item.metadata).status || '').toLowerCase();
  if (s === 'deprecated') return 'deprecated';
  if (s === 'planned') return 'planned';
  return 'live';
}

// Estado vacio amigable con CTA opcional (evita lenguaje BD).
function emptyState(msg, ctaLabel, ctaHref) {
  return `<div class="pdt-empty">
    <div>${msg}</div>
    ${ctaLabel ? `<div style="margin-top:10px"><a class="btn btn-ghost btn-sm" href="${ctaHref}">${esc(ctaLabel)}</a></div>` : ''}
  </div>`;
}

// #2454(b): misiones activas por proyecto via mapeo pragmatico de prefijo de code.
// CONCILIACION-* toca api+panel (multi). Devuelve true si la mision pertenece al proyecto.
const MISSION_PREFIX_MAP = [
  { re: /^DESKTOP-/,        keys: ['nexo-desktop'] },
  { re: /^PANEL-/,          keys: ['panel-v2'] },
  { re: /^(NEXO-)?API-/,    keys: ['nexo-api'] },
  { re: /^WEB-/,            keys: ['nexo-web'] },
  { re: /^ARGY-DOC-/,       keys: ['nexo-center'] },
  { re: /^CONCILIACION-/,   keys: ['nexo-api', 'panel-v2'] },
];
function missionBelongsToProject(code, projectKey) {
  const c = String(code || '').toUpperCase();
  for (const m of MISSION_PREFIX_MAP) {
    if (m.re.test(c)) return m.keys.includes(projectKey);
  }
  return false;
}

// La pestana Misiones solo muestra estas. Q #33064: el detalle pedia UNA pagina de 200 sin filtro y la base tiene 317
// misiones; una viva fuera de esa pagina no salia en su proyecto. Ahora se piden solo las vivas, por estado y todas
// las paginas (la API topa pageSize en 200: MissionService.PageSizeMaximo).
const ESTADOS_DE_LA_PESTANA = ['in_progress', 'active', 'por_cerrar'];
const MISIONES_POR_PAGINA = 200;
const TOPE_DE_PAGINAS = 10;

// Lo que la API entrega de verdad por pedido de tareas (TasksController recorta pageSize a 50).
const TAREAS_POR_ESTADO = 50;

async function traerMisionesVivas() {
  const items = [];
  let cortado = false;
  for (const status of ESTADOS_DE_LA_PESTANA) {
    let completo = false;
    for (let page = 1; page <= TOPE_DE_PAGINAS; page++) {
      const res = await MissionApiClient.list(page, MISIONES_POR_PAGINA, { status });
      if (!res.ok) return { ok: false, error: res.error };
      const d = res.data?.data || res.data || {};
      const lote = d.items || [];
      items.push(...lote);
      const total = Number(d.totalCount);
      if (!lote.length || (Number.isFinite(total) ? items.filter((m) => m.status === status).length >= total : lote.length < MISIONES_POR_PAGINA)) { completo = true; break; }
    }
    // Si se corta por el tope de paginas, la pestana lo dice: puede haber mas misiones de las que se ven.
    if (!completo) cortado = true;
  }
  return { ok: true, data: { items, cortado } };
}

// Lo servido de cada proyecto. El escritorio no tiene sello que el navegador pueda leer: lo lee la API en el servidor
// (GET salud/escritorio-version, A #39229). Si no contesta, queda sin sello y la cabecera dice "pendiente de la API".
async function selloDeProyecto(key) {
  if (key !== 'nexo-desktop') return selloDe(key);
  const r = await SaludApiClient.getEscritorioVersion();
  if (!r.ok) return { estado: 'sin-sello', version: '', commit: '', extra: '', porque: r.motivo };
  const cuando = r.data.leidoAt ? formatearFecha(r.data.leidoAt, {}, { parte: 'todo' }) : '';
  return { estado: 'servido', version: r.data.version, commit: '', extra: cuando ? `manifiesto leído ${cuando}` : '',
    porque: '', fuente: 'manifiesto publicado, leído por la API (salud/escritorio-version)' };
}

// ─── componente principal ────────────────────────────────────────────────────

export async function renderProjectDetail(container) {
  const key = decodeURIComponent(getKeyFromHash());
  if (!key) {
    container.innerHTML = '<p class="empty-state">Project key invalida</p>';
    return;
  }

  container.innerHTML = `
    <section class="artifact-view">
      ${regresoHtml({ volverA: "#/projects", texto: "Proyectos" })}
      <div id="proj-detail-loading" class="empty-state">Cargando proyecto <code>${esc(key)}</code>…</div>
      <div id="proj-detail-root" hidden></div>
    </section>`;

  // Cargar en paralelo: proyecto + items vinculados + instrucciones + misiones+tareas + bitacora; y lo medido
  // (PROYECTOS-ALINEADOS-T1): la foto de salud con dueno y fecha, y el sello de lo servido.
  const [resProject, resItems, resInstructions, resMissions, resTasks, resBitacora, resArq, resTrabajo, resRespaldo, sello] = await Promise.all([
    ProjectApiClient.getByKey(key),
    ProjectItemsApiClient.getByKey(key),
    InstructionApiClient.list(1, 200),
    traerMisionesVivas(),
    TaskApiClient.list(1, 500),
    BitacoraApiClient.list(80),
    SaludApiClient.getVigentes('arquitectura'),
    SaludApiClient.getVigentes('trabajo'),
    SaludApiClient.getVigentes('respaldo'),
    selloDeProyecto(key),
  ]);
  const filasDe = (r) => (r.ok ? r.data.map(leerFila) : []);
  const salud = { filas: [...filasDe(resArq), ...filasDe(resTrabajo)], respaldo: filasDe(resRespaldo), sello,
    motivo: resArq.ok ? '' : (resArq.detalle || resArq.motivo || '') };

  // La bitacora del proyecto se pide por su dueno: los ultimos 80 de toda la flota casi nunca nombran el repo
  // (medido 31-oct: 0 de 80 decian nexo-desktop y D publico 171 eventos en 24 h). La API filtra por igualdad exacta.
  const dueno = duenoDelRepo(salud.filas, key);
  // Operacional (Ares #39430): los [MERGE]/[VIVO] de Ares que nombran el proyecto y las tareas abiertas del dueno
  // (la API filtra assignee y status; la lista sin filtro de 500 no alcanza: 2781 tareas, 416 abiertas el 31-oct).
  const [delDueno, deAres, tareasDueno] = await Promise.all([
    Promise.all(origenesDelDueno(dueno).map((o) => BitacoraApiClient.list(100, null, o))),
    BitacoraApiClient.list(100, null, 'ARES-001'),
    dueno
      ? Promise.all(['in_progress', 'pending'].map((st) => TaskApiClient.list(1, TAREAS_POR_ESTADO, st, dueno)))
      : Promise.resolve([]),
  ]);

  if (!resProject.ok) {
    document.getElementById('proj-detail-loading').innerHTML =
      `<p class="empty-state">No se pudo cargar el proyecto: ${esc(resProject.error || 'error desconocido')}</p>`;
    return;
  }

  const project       = resProject.data?.data || resProject.data;
  const itemsResp     = (resItems.ok ? (resItems.data?.data || resItems.data) : null) || { canon: null, byType: {}, all: [], totalCount: 0 };
  const instructions  = (resInstructions.ok ? (resInstructions.data?.data?.items || resInstructions.data?.items) : []) || [];
  const allMissions   = (resMissions.ok    ? (resMissions.data?.data?.items    || resMissions.data?.items)    : []) || [];
  const allTasks      = (resTasks.ok       ? (resTasks.data?.data?.items       || resTasks.data?.items)       : []) || [];
  const itemsDe       = (r) => (r.ok ? (r.data?.data?.items || r.data?.items) : []) || [];
  const general       = itemsDe(resBitacora);
  const eventosDueno  = delDueno.flatMap(itemsDe);
  // Union sin repetir, lo mas nuevo primero: lo del dueno + lo de cualquiera que nombre el proyecto.
  const vistos = new Set();
  const bitacora = [...eventosDueno, ...general]
    .filter((b) => (vistos.has(b.id) ? false : vistos.add(b.id)))
    .sort((a, b) => Number(b.id) - Number(a.id));
  const origenesDueno = new Set(origenesDelDueno(dueno));

  // Indice code -> instruccion completa para canon link
  const instByCode = new Map();
  for (const i of instructions) {
    const code = i.code || i.instructionId || i.instruction_id;
    if (code) instByCode.set(String(code).toUpperCase(), i);
  }

  const root = document.getElementById('proj-detail-root');
  document.getElementById('proj-detail-loading').remove();
  root.hidden = false;

  const misionesCortadas = !!(resMissions.ok && resMissions.data?.cortado);
  const misionesDelDueno = misionesDeLosEventos(eventosDueno);
  const vistosOp = new Set();
  const eventosOperacional = [...eventosDueno, ...itemsDe(deAres), ...general]
    .filter((b) => (vistosOp.has(b.id) ? false : vistosOp.add(b.id)));
  const tareasDelDueno = tareasDueno.flatMap(itemsDe);
  // La API recorta pageSize a 50 (TasksController:101) y manda totalCount del filtro entero (Q #39480, A #39486).
  const tareasTotal = tareasDueno.reduce((n, r) => n + (Number((r.ok && (r.data?.data?.totalCount ?? r.data?.totalCount))) || 0), 0);
  renderShell(root, { project, itemsResp, instByCode, allMissions, misionesCortadas, allTasks, bitacora,
    salud, dueno, origenesDueno, misionesDelDueno, eventosOperacional, tareasDelDueno, tareasTotal });
}

// ─── shell con header + tabs ─────────────────────────────────────────────────

function renderShell(root, ctx) {
  const { project, itemsResp } = ctx;
  const total = itemsResp.totalCount || 0;
  const canonExists = !!itemsResp.canon;

  const tabs = [
    { id: 'canon',         label: canonExists ? 'Canon' : 'Canon', extra: canonExists ? '✓' : '' },
    { id: 'items',         label: 'Items',         extra: String(total) },
    { id: 'missions',      label: 'Misiones',      extra: '' },
    { id: 'bitacora',      label: 'Bitácora',      extra: '' },
    { id: 'instructions',  label: 'Instrucciones', extra: String((itemsResp.byType?.instruction || []).length + (canonExists ? 1 : 0)) },
    { id: 'operational',   label: 'Operacional',   extra: '' },
    { id: 'docs',          label: 'Docs copiados a Center', extra: docCoverageBadge(project) },
  ];

  const preguntas = cincoPreguntas({ projectKey: project.projectKey, filas: ctx.salud.filas, sello: ctx.salud.sello,
    misiones: misionesDelProyecto(ctx) });
  const servida = preguntas[0];
  const respaldo = lineaRespaldo(ctx.salud.respaldo);

  root.innerHTML = `
    <div class="proj-detail-inner">
      <div class="pdt-respaldo pdt-est-${respaldo.estado}" data-pdt-respaldo title="${esc(respaldo.fuente)}">${esc(respaldo.texto)}</div>
      <div class="proj-detail-head">
        <div>
          <div class="proj-detail-title">${esc(project.displayName || project.projectKey)}</div>
          <div style="font-size:0.875rem;color:#8b949e;margin-top:2px">
            <code class="proj-key-sm">${esc(project.projectKey)}</code>
            · <span data-pdt-servida class="pdt-est-${servida.estado}" title="${esc(servida.fuente)}">versión servida: ${esc(servida.valor)}</span>
            · ${badge(project.operationalStatus || project.status)}
            · ${parseFloat(project.progressPercent || 0).toFixed(0)}%
          </div>
          <div style="margin-top:6px;max-width:420px">${pctBar(project.progressPercent)}</div>
          ${anotadaHtml(project)}
        </div>
        <div style="display:flex;gap:.5rem;align-items:start">
          <a href="#/projects" class="btn btn-ghost btn-sm">Editar en lista</a>
        </div>
      </div>

      ${ctx.salud.motivo ? `<p class="pdt-salud-motivo">No se pudo leer la foto de salud: ${esc(ctx.salud.motivo)}</p>` : ''}
      <div class="pdt-salud" data-pdt-salud>
        ${preguntas.map((p) => `
          <div class="pdt-pregunta pdt-est-${p.estado}" data-pdt-pregunta="${p.id}" title="${esc(p.fuente)}">
            <div class="pdt-pregunta-q">${esc(p.pregunta)}</div>
            <div class="pdt-pregunta-v">${esc(p.valor)}${p.estado === 'vencida' ? ' <span class="pdt-vencida">vencida</span>' : ''}</div>
            <div class="pdt-pregunta-d">${esc(p.detalle)}</div>
            <div class="pdt-pregunta-f">${esc(p.fuente)}</div>
          </div>`).join('')}
      </div>

      <div class="proj-tabs" id="proj-tabs" role="tablist" style="display:flex;gap:4px;border-bottom:1px solid #21262d;margin-top:14px;flex-wrap:wrap">
        ${tabs.map((t, i) => `
          <button role="tab" class="proj-tab ${i === 0 ? 'active' : ''}" data-tab="${t.id}"
                  style="background:transparent;border:none;border-bottom:2px solid transparent;color:#c9d1d9;padding:8px 12px;cursor:pointer;font-size:0.875rem">
            ${esc(t.label)}${t.extra ? `&nbsp;<span style="background:#21262d;border-radius:4px;padding:1px 6px;font-size:0.875rem;color:#8b949e">${esc(t.extra)}</span>` : ''}
          </button>`).join('')}
      </div>

      <div id="proj-tab-body" style="margin-top:14px"></div>
    </div>

    <style>
      .proj-tab.active { color:#58a6ff !important; border-bottom-color:#58a6ff !important; font-weight:600; }
      .proj-tab:hover  { color:#58a6ff; }
      .pdt-card        { padding:12px;border:1px solid #21262d;border-radius:8px;background:#161b22;margin-bottom:10px; }
      .pdt-card h4     { margin:0 0 6px 0;font-size:.92rem;color:#c9d1d9; }
      .pdt-card .meta  { font-size:0.875rem;color:#8b949e;display:flex;flex-wrap:wrap;gap:8px;margin-top:4px; }
      .pdt-row         { display:flex;justify-content:space-between;align-items:center;padding:6px 0;border-bottom:1px solid #161b22; }
      .pdt-row:last-child { border-bottom:none; }
      .pdt-empty       { padding:14px;color:#6e7681;text-align:center;font-size:0.875rem;border:1px dashed #21262d;border-radius:8px; }
      .pdt-grp         { margin-bottom:10px; }
      .pdt-grp-title   { display:flex;justify-content:space-between;align-items:center;padding:6px 10px;background:#161b22;border-radius:6px;margin-bottom:6px;font-size:0.875rem;color:#c9d1d9; }
      .pdt-grp-count   { background:#21262d;border-radius:4px;padding:1px 7px;font-size:0.875rem;color:#8b949e; }
      .pdt-respaldo    { padding:6px 10px;border-radius:6px;font-size:0.875rem;margin-bottom:10px;background:#16261a;color:#3fb950;border:1px solid #23863655; }
      .pdt-respaldo.pdt-est-sin-respaldo, .pdt-respaldo.pdt-est-sin-medir { background:#2a1517;color:#f85149;border-color:#f8514955; }
      .pdt-respaldo.pdt-est-vencida { background:#2a2214;color:#d29922;border-color:#d2992255; }
      .pdt-anotada     { font-size:0.875rem;color:#6e7681;margin-top:4px; }
      .pdt-salud       { display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:8px;margin-top:12px; }
      .pdt-pregunta    { padding:10px 12px;border:1px solid #21262d;border-radius:8px;background:#161b22;min-width:0; }
      .pdt-pregunta-q  { font-size:0.875rem;color:#8b949e; }
      .pdt-pregunta-v  { font-size:1.1rem;font-weight:700;color:#c9d1d9;margin-top:4px;overflow-wrap:anywhere; }
      .pdt-pregunta-d  { font-size:0.875rem;color:#8b949e;margin-top:4px;overflow-wrap:anywhere; }
      .pdt-pregunta-f  { font-size:0.875rem;color:#6e7681;margin-top:4px;overflow-wrap:anywhere; }
      .pdt-pregunta.pdt-est-sin-medir .pdt-pregunta-v, .pdt-pregunta.pdt-est-pendiente .pdt-pregunta-v { color:#6e7681;font-weight:600; }
      .pdt-pregunta.pdt-est-vencida   { opacity:.75; }
      .pdt-vencida     { font-size:0.875rem;font-weight:600;color:#d29922;margin-left:6px; }
      .pdt-salud-motivo { font-size:0.875rem;color:#d29922;margin:10px 0 0; }
    </style>`;

  // wire tabs
  const $tabs = root.querySelectorAll('.proj-tab');
  const $body = document.getElementById('proj-tab-body');
  $tabs.forEach(btn => {
    btn.addEventListener('click', () => {
      $tabs.forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      renderTab(btn.dataset.tab, $body, ctx);
    });
  });

  // primer tab
  renderTab('canon', $body, ctx);
}

// ─── render por pestana ──────────────────────────────────────────────────────

function renderTab(tabId, $body, ctx) {
  const renderer = ({
    canon:        renderCanonTab,
    items:        renderItemsTab,
    missions:     renderMissionsTab,
    bitacora:     renderBitacoraTab,
    instructions: renderInstructionsTab,
    operational:  renderOperationalTab,
    docs:         renderDocsTab,
  })[tabId];
  if (!renderer) {
    $body.innerHTML = '<p class="pdt-empty">Pestana no implementada</p>';
    return;
  }
  renderer($body, ctx);
}

// ─── tab Canon ───────────────────────────────────────────────────────────────

function renderCanonTab($body, { itemsResp, instByCode }) {
  const canon = itemsResp.canon;
  if (!canon) {
    $body.innerHTML = emptyState(
      'Este proyecto aun no tiene una <strong>instruccion rectora (canon)</strong> que defina su modelo, vocabulario y reglas.',
      'Ver todas las instrucciones', '#/instructions');
    return;
  }

  const meta = safeJsonParse(canon.metadata);
  const inst = instByCode.get(String(canon.itemRef).toUpperCase());
  const instId = inst?.id;

  $body.innerHTML = `
    <div class="pdt-card" style="border-left:3px solid ${RELATION_COLOR.canon}">
      <div style="display:flex;justify-content:space-between;align-items:start;gap:10px">
        <div style="flex:1">
          <h4>📜 ${esc(meta.title || canon.itemRef)} ${relBadge('canon')}</h4>
          <div style="font-size:0.875rem;color:#c9d1d9;margin-top:4px">
            <code class="proj-key-sm">${esc(canon.itemRef)}</code>
            ${meta.version ? ` · <span style="color:#a371f7">${esc(meta.version)}</span>` : ''}
          </div>
          ${meta.note ? `<p style="margin-top:8px;font-size:0.875rem;color:#c9d1d9;line-height:1.5">${esc(meta.note)}</p>` : ''}
          ${meta.file_path ? `<div class="meta"><strong>Archivo:</strong> <code>${esc(meta.file_path)}</code></div>` : ''}
        </div>
        <div style="display:flex;flex-direction:column;gap:6px;flex-shrink:0">
          ${instId
            ? `<a class="btn btn-primary btn-sm" href="#/instructions/${esc(instId)}">Abrir canon</a>`
            : `<span class="proj-badge" style="background:#d2992222;color:#d29922">No indexada</span>`
          }
          <a class="btn btn-ghost btn-sm" href="#/instructions">Ver todas</a>
        </div>
      </div>
    </div>
    <p style="font-size:0.875rem;color:#6e7681;margin-top:8px">
      El canon es la <strong>instruccion rectora</strong> del proyecto: define modelo, vocabulario y reglas arquitectonicas no negociables. Cualquier cambio al modelo debe pasar por actualizar este documento.
    </p>`;
}

// ─── tab Items (master-detail: categorias + lista + busqueda) ─────────────────
// #2454(a): la lista infinita era tediosa. Selector de categorias a la izquierda
// (con conteos) + lista de la categoria a la derecha + busqueda global arriba.
function renderItemsTab($body, { itemsResp }) {
  const byType = itemsResp.byType || {};
  const all = [];
  for (const type of Object.keys(byType)) {
    for (const it of (byType[type] || [])) all.push({ ...it, _type: type });
  }
  if (all.length === 0) {
    $body.innerHTML = emptyState(
      'Este proyecto todavia no tiene componentes, vistas ni endpoints vinculados.',
      'Ver todos los proyectos', '#/projects');
    return;
  }

  // Categorias: vivos por familia de tipo + Planificados/Archivados por estado.
  const CATS = [
    { id: 'instruction', label: 'Instrucciones', icon: '📜', match: it => itemStatusOf(it) === 'live' && it._type === 'instruction' },
    { id: 'form',        label: 'Formularios',   icon: '📝', match: it => itemStatusOf(it) === 'live' && it._type === 'form' },
    { id: 'component',   label: 'Componentes',   icon: '🧩', match: it => itemStatusOf(it) === 'live' && it._type === 'component' },
    { id: 'service',     label: 'Servicios',     icon: '🔌', match: it => itemStatusOf(it) === 'live' && ['endpoint', 'workflow', 'view', 'infrastructure', 'table', 'capability'].includes(it._type) },
    // PANEL-ENDPOINTS-FAMILIAS-T1 (Josse 17-sep): los endpoints (vivos y planificados) agrupados por familia, con quien
    // los consume, su guardia y su uso. Los archivados no.
    { id: 'familias',    label: 'Endpoints por familia', icon: '🧭', match: it => it._type === 'endpoint' && itemStatusOf(it) !== 'deprecated' },
    // SALV-T9: lo que a Josse le falta mirar. Cruza las familias (un nodo sin verificar sigue en la suya); los
    // archivados no cuentan.
    { id: 'sinverificar', label: 'Sin verificar', icon: '⏳', match: it => itemStatusOf(it) !== 'deprecated' && !selloDeVerificacion(safeJsonParse(it.metadata)).verificado },
    { id: 'planned',     label: 'Planificados',  icon: '🔮', match: it => itemStatusOf(it) === 'planned' },
    { id: 'archived',    label: 'Archivados',    icon: '🗄️', match: it => itemStatusOf(it) === 'deprecated' },
  ];
  const buckets = {};
  for (const c of CATS) buckets[c.id] = all.filter(c.match);
  const visibleCats = CATS.filter(c => buckets[c.id].length > 0);
  let activeCat = (visibleCats[0] || CATS[0]).id;
  let query = '';

  $body.innerHTML = `
    <input type="search" data-it-search placeholder="🔎 Buscar por codigo, nombre o ruta…"
           class="form-control form-control-sm" style="width:100%;margin-bottom:10px">
    <div style="display:flex;gap:14px;align-items:flex-start;flex-wrap:wrap">
      <div data-it-cats style="flex:0 0 210px;min-width:170px;display:flex;flex-direction:column;gap:4px"></div>
      <div data-it-list style="flex:1;min-width:250px"></div>
    </div>`;

  const $search = $body.querySelector('[data-it-search]');
  const $cats = $body.querySelector('[data-it-cats]');
  const $list = $body.querySelector('[data-it-list]');

  function renderCats() {
    $cats.innerHTML = visibleCats.map(c => {
      const on = activeCat === c.id && !query;
      return `
        <button type="button" data-it-cat="${c.id}"
          style="display:flex;justify-content:space-between;align-items:center;gap:8px;padding:8px 10px;border-radius:6px;cursor:pointer;font-size:0.875rem;text-align:left;border:1px solid ${on ? '#58a6ff' : '#21262d'};background:${on ? '#1f6feb22' : '#161b22'};color:${on ? '#58a6ff' : '#c9d1d9'}">
          <span>${c.icon} ${esc(c.label)}</span>
          <span class="pdt-grp-count">${buckets[c.id].length}</span>
        </button>`;
    }).join('');
    $cats.querySelectorAll('[data-it-cat]').forEach(btn => {
      btn.addEventListener('click', () => {
        activeCat = btn.dataset.itCat;
        if (query) { query = ''; $search.value = ''; }
        renderCats(); renderList();
      });
    });
  }

  function matchesQuery(it) {
    if (!query) return true;
    const m = safeJsonParse(it.metadata);
    const hay = `${it.itemRef} ${m.note || ''} ${m.title || ''} ${m.file_path || m.route || m.table || ''}`.toLowerCase();
    return hay.includes(query);
  }

  function renderList() {
    // Con busqueda: resultados GLOBALES (todas las categorias). Sin busqueda: la categoria activa.
    if (query) {
      const results = all.filter(matchesQuery);
      $list.innerHTML = results.length
        ? `<div style="font-size:0.875rem;color:#8b949e;margin-bottom:6px">${results.length} resultado(s) para “${esc(query)}”</div>`
          + results.map(i => renderItemCard(i)).join('')
        : `<p class="pdt-empty">Sin resultados para “${esc(query)}”.</p>`;
      return;
    }
    const items = buckets[activeCat] || [];
    if (activeCat === 'familias' && items.length) { $list.innerHTML = endpointsPorFamiliaHtml(items); return; }
    $list.innerHTML = items.length
      ? items.map(i => renderItemCard(i)).join('')
      : '<p class="pdt-empty">Sin elementos en esta categoria.</p>';
  }

  $search.addEventListener('input', () => {
    query = $search.value.trim().toLowerCase();
    renderCats(); renderList();
  });
  renderCats();
  renderList();
}

/**
 * PANEL-ENDPOINTS-FAMILIAS-T1: los endpoints agrupados por familia (application/proyectos/familiasEndpoints.js), cada
 * familia con su conteo y cada tarjeta con consumidores, guardia y uso. Lo que el nodo no trae se dice "sin dato".
 * Exportada para el guardia (check-endpoints-familias).
 */
export function endpointsPorFamiliaHtml(items) {
  const porRef = new Map(items.map(i => [i.itemRef, i]));
  const grupos = agruparPorFamilia(items.map(i => ({ itemRef: i.itemRef, meta: safeJsonParse(i.metadata) })));
  const consHtml = (c) => {
    if (c.estado === 'sin_dato') return '<span data-ep-cons="sin_dato" style="color:#8b949e">Consumidores: sin dato en el nodo</span>';
    if (c.estado === 'ninguno') return `<span data-ep-cons="ninguno" style="color:#d29922">Sin consumidor conocido${c.lista[0]?.archivo ? ` (${esc(c.lista[0].archivo)})` : ''}</span>`;
    return `<span data-ep-cons="lista">Consumidores: ${c.lista.map(x => `${esc(x.artefacto)}${x.archivo ? ` <code style="font-size:0.875rem">${esc(x.archivo)}</code>` : ''}`).join(' · ')}</span>`;
  };
  return `<div data-ep-familias style="font-size:0.875rem;color:#8b949e;margin-bottom:6px">${items.length} endpoints en ${grupos.length} familias</div>`
    + grupos.map(g => `<section data-ep-familia="${esc(g.familia)}" style="margin-bottom:12px">
        <h4 style="margin:0 0 6px;font-size:0.875rem;color:#c9d1d9">${esc(g.familia)} <span class="pdt-grp-count">${g.items.length}</span></h4>`
      + g.items.map(x => {
        const guardia = String(x.meta.guardia || '').trim();
        const uso = [x.uso.ultimo && `ultimo uso: ${x.uso.ultimo}`, x.uso.texto && `llamadas: ${x.uso.texto}`].filter(Boolean).join(' · ');
        const extra = `<div data-ep-extra style="font-size:0.875rem;color:#c9d1d9;margin:-4px 0 8px 12px;padding:6px 10px;border-left:2px solid #30363d">`
          + `<div>${consHtml(x.consumidores)}</div>`
          + `<div data-ep-guardia style="color:#8b949e">Guardia: ${guardia ? esc(guardia) : 'sin dato en el nodo'}</div>`
          + `<div data-ep-uso style="color:#8b949e">Uso: ${uso ? esc(uso) : 'sin dato en el nodo'}${x.excesivo ? ' <b data-ep-excesivo style="color:#f85149">excesivo (10 veces la mediana de su familia)</b>' : ''}</div>`
          + '</div>';
        return renderItemCard(porRef.get(x.itemRef)) + extra;
      }).join('') + '</section>').join('');
}

function renderItemCard(item) {
  const meta = safeJsonParse(item.metadata);
  const where = meta.file_path || meta.route || meta.table || meta.external_id || '';
  const ver = meta.version || '';
  const note = meta.note || meta.title || '';
  const st = itemStatusOf(item);
  const stMeta = ITEM_STATUS[st];         // undefined cuando 'live'
  const archived = st === 'deprecated';
  // SALV-T9: quien verifico este nodo y cuando (vacio = sin verificar), lo que dijo que NO comprobo, y su dueno.
  const sello = selloDeVerificacion(meta);
  const dueno = duenoDelNodo(meta);
  const selloHtml = sello.verificado
    ? `<div data-sello="verificado" style="font-size:0.875rem;color:#3fb950;margin-top:3px">✔ Verificado${sello.cuando ? ` el ${esc(cuandoLegible(sello.cuando))}` : ''} por ${esc(sello.quien)}</div>`
    : `<div data-sello="sin-verificar" style="font-size:0.875rem;color:#d29922;margin-top:3px">⏳ Sin verificar</div>`;
  const faltaHtml = sello.falta ? `<div data-sello-falta style="font-size:0.875rem;color:#d29922;margin-top:2px">Falta comprobar: ${esc(sello.falta)}</div>` : '';
  const duenoHtml = `<div data-dueno style="font-size:0.875rem;color:#8b949e;margin-top:2px">Dueño: ${dueno ? esc(dueno) : 'sin dueño registrado'}</div>`;
  return `
    <div class="pdt-card" style="padding:8px 12px;margin-bottom:6px">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
        <div style="flex:1;min-width:200px">
          <div style="font-size:0.875rem;color:#c9d1d9">
            <code class="proj-key-sm"${archived ? ' style="text-decoration:line-through;color:#8b949e"' : ''}>${esc(item.itemRef)}</code>
            ${ver ? ` <span style="color:#a371f7;font-size:0.875rem">v${esc(ver)}</span>` : ''}
            ${relBadge(item.relation)}
            ${stMeta ? ` <span class="proj-badge" style="background:${stMeta.color}22;color:${stMeta.color};font-size:0.875rem">${stMeta.icon} ${stMeta.label}</span>` : ''}
          </div>
          ${note ? `<div style="font-size:0.875rem;color:#8b949e;margin-top:3px">${esc(note)}</div>` : ''}
          ${selloHtml}${faltaHtml}${duenoHtml}
        </div>
        ${where ? `<code style="font-size:0.875rem;color:#6e7681">${esc(where)}</code>` : ''}
      </div>
    </div>`;
}

// ─── tab Misiones ────────────────────────────────────────────────────────────

// #2454(b): SOLO misiones vivas, nunca completadas/archivadas. por_cerrar (MISIONES-CANDADOS-T1) sigue sin
// [VERIFICADO]: va aqui, al final, para que se vea lo que espera cierre.
// Una mision es del proyecto por prefijo de code (mapeo pragmatico), por mencion explicita del proyecto, o porque el
// dueno del repo la nombra en sus eventos (PROYECTOS-ALINEADOS-T1: ARQUITECTURA-CAPAS-V1-001 y PARIDAD son de varios
// repos y ninguna de las dos reglas viejas las unia).
function misionesDelProyecto({ project, allMissions, misionesDelDueno }) {
  const key = (project.projectKey || '').toLowerCase();
  const ACTIVE = new Set(ESTADOS_DE_LA_PESTANA);
  return allMissions.filter(m => {
    if (!ACTIVE.has(String(m.status || '').toLowerCase())) return false;
    const code = m.code || m.missionCode || m.mission_code || '';
    const byPrefix = missionBelongsToProject(code, project.projectKey);
    const inText = String(m.description || m.title || '').toLowerCase().includes(key);
    const delDueno = !!(misionesDelDueno && misionesDelDueno.has(String(code).toUpperCase()));
    return byPrefix || inText || delDueno;
  });
}

function renderMissionsTab($body, ctx) {
  const { misionesCortadas } = ctx;
  const avisoTope = misionesCortadas
    ? `<p style="font-size:0.875rem;color:#d29922;margin:0 0 8px">Se corto por el tope de paginas: puede haber mas misiones de este proyecto que no se ven aqui.</p>`
    : '';
  const matched = misionesDelProyecto(ctx);

  if (matched.length === 0) {
    $body.innerHTML = avisoTope + emptyState('Este proyecto no tiene misiones activas por ahora.', 'Ver todas las misiones', '#/missions');
    return;
  }

  matched.sort((a, b) => {
    const sa = (a.status || '').toLowerCase(); const sb = (b.status || '').toLowerCase();
    const order = { in_progress: 0, active: 1, por_cerrar: 2 };
    return (order[sa] ?? 9) - (order[sb] ?? 9);
  });

  $body.innerHTML = avisoTope + `
    <table class="proj-deps-table" style="width:100%">
      <thead><tr>
        <th style="text-align:left;padding:6px 8px;font-size:0.875rem;color:#8b949e">Codigo</th>
        <th style="text-align:left;padding:6px 8px;font-size:0.875rem;color:#8b949e">Titulo</th>
        <th style="text-align:left;padding:6px 8px;font-size:0.875rem;color:#8b949e">Estado</th>
        <th style="text-align:right;padding:6px 8px;font-size:0.875rem;color:#8b949e">NXS</th>
      </tr></thead>
      <tbody>
        ${matched.map(m => `
          <tr>
            <td style="padding:6px 8px"><code class="proj-key-sm">${esc(m.code || m.missionCode || '—')}</code></td>
            <td style="padding:6px 8px;font-size:0.875rem">
              ${m.id ? `<a href="#/missions/${esc(m.id)}" style="color:#58a6ff">${esc(m.title || '(sin titulo)')}</a>` : esc(m.title || '(sin titulo)')}
            </td>
            <td style="padding:6px 8px">${badge(m.status)}</td>
            <td style="padding:6px 8px;text-align:right;font-size:0.875rem">${esc(m.rewardNexos ?? m.reward_nexos ?? '—')}</td>
          </tr>`).join('')}
      </tbody>
    </table>`;
}

// ─── tab Bitacora ────────────────────────────────────────────────────────────

// Lo que escribio el dueno del repo + lo de cualquiera que nombre el proyecto (PROYECTOS-ALINEADOS-T1).
function renderBitacoraTab($body, { project, bitacora, dueno, origenesDueno }) {
  const key = (project.projectKey || '').toLowerCase();
  const matched = bitacora.filter(b => {
    if (origenesDueno && origenesDueno.has(b.origen)) return true;
    const t = String(b.titulo || '').toLowerCase();
    const n = String(typeof b.notas === 'string' ? b.notas : JSON.stringify(b.notas || '')).toLowerCase();
    return t.includes(key) || n.includes(key);
  }).slice(0, 20);

  const deQuien = dueno
    ? `Lo último del dueño del repo (${esc(dueno)}) y de quien nombre <code>${esc(project.projectKey)}</code>.`
    : `Lo último que nombra <code>${esc(project.projectKey)}</code>: este repo todavía no tiene dueño en la foto de salud.`;

  if (matched.length === 0) {
    $body.innerHTML = `<p class="pdt-bit-de" style="font-size:0.875rem;color:#8b949e;margin:0 0 8px">${deQuien}</p>`
      + emptyState('Sin actividad reciente registrada para este proyecto.');
    return;
  }

  $body.innerHTML = `<p class="pdt-bit-de" style="font-size:0.875rem;color:#8b949e;margin:0 0 8px">${deQuien}</p>` + matched.map(b => {
    const ts = b.tsUtc || b.ts_utc || '';
    const fecha = ts ? formatearFecha(ts, {}, { parte: 'todo' }) : '';
    return `
      <div class="pdt-card" style="padding:8px 12px;margin-bottom:6px">
        <div style="display:flex;justify-content:space-between;gap:10px;align-items:start">
          <div style="flex:1;min-width:200px">
            <div style="font-size:0.875rem;color:#c9d1d9">${esc(b.titulo || '(sin titulo)')}</div>
            <div style="font-size:0.875rem;color:#6e7681;margin-top:3px">
              <code>#${esc(b.id)}</code> · ${esc(b.origen || '?')} · ${esc(fecha)}
            </div>
          </div>
        </div>
      </div>`;
  }).join('');
}

// ─── tab Instrucciones ───────────────────────────────────────────────────────

function renderInstructionsTab($body, { itemsResp, instByCode }) {
  const all = [];
  if (itemsResp.canon) all.push(itemsResp.canon);
  for (const i of (itemsResp.byType?.instruction || [])) all.push(i);

  if (all.length === 0) {
    $body.innerHTML = emptyState('Este proyecto no tiene instrucciones vinculadas todavia.', 'Ver todas las instrucciones', '#/instructions');
    return;
  }

  $body.innerHTML = all.map(item => {
    const meta = safeJsonParse(item.metadata);
    const inst = instByCode.get(String(item.itemRef).toUpperCase());
    const instId = inst?.id;
    const isCanon = item.relation === 'canon';
    return `
      <div class="pdt-card" style="padding:10px 12px;margin-bottom:6px;${isCanon ? `border-left:3px solid ${RELATION_COLOR.canon}` : ''}">
        <div style="display:flex;justify-content:space-between;align-items:start;gap:10px;flex-wrap:wrap">
          <div style="flex:1;min-width:240px">
            <div style="font-size:.88rem;color:#c9d1d9">
              <code class="proj-key-sm">${esc(item.itemRef)}</code>
              ${meta.version ? `<span style="color:#a371f7;font-size:0.875rem;margin-left:6px">${esc(meta.version)}</span>` : ''}
              ${relBadge(item.relation)}
            </div>
            ${meta.title ? `<div style="font-size:0.875rem;color:#8b949e;margin-top:3px">${esc(meta.title)}</div>` : ''}
          </div>
          ${instId
            ? `<a href="#/instructions/${esc(instId)}" class="btn btn-ghost btn-sm" style="flex-shrink:0">Abrir</a>`
            : `<span class="proj-badge" style="background:#21262d;color:#6e7681;font-size:0.875rem">No indexada en BD</span>`
          }
        </div>
      </div>`;
  }).join('');
}

// ─── tab Operacional (editable) ──────────────────────────────────────────────
//
// Replica los inputs editables que tenia ProjectListView (vista vieja) y persiste
// via ProjectApiClient.updateByKey (PUT /api/v1/Projects/by-key/{key}). Reemplaza
// la pestana Operacional read-only original. Mantiene mismo schema de payload:
// description, goals, status, operationalStatus, technologies (no editable aqui),
// progressPercent (no editable aqui), statusMetadata (recentChanges, nextSteps,
// blockers, notes).
//
// Nota: currentVersion, activeBranch, deployUrl, lastDeployAt NO los acepta
// UpdateProjectRequest del backend (verificado en NexoAPI/Models/ProjectDtos.cs).
// La vista vieja los enviaba pero el backend los ignoraba silenciosamente. Aqui
// los renderizamos como read-only con un aviso, para no prometer persistencia
// que no existe.

// ─── tab Documentacion ────────────────────────────────────────────────────────
function renderDocsTab($body, ctx) {
  const { project } = ctx;
  const { data: dc, sinAuditoria } = docCoverageOf(project);
  if (sinAuditoria) {
    $body.innerHTML = emptyState('Sin auditoría: este proyecto no tiene la cuenta de docs copiados a Nexo Center. '
      + 'Esto no son los contratos de los nodos: esos van en «¿Cada pieza dice qué promete?».');
    return;
  }
  const pct = docCoveragePct(dc);
  const pctColor = pct >= 70 ? '#238636' : pct >= 35 ? '#d29922' : '#8b949e';
  const mdN = dc.docs_md ?? '—';
  const centerN = docsEnCenter(dc);
  const pend = docsPendientes(dc);
  const alin = alineadaState(dc.carpeta_principal_alineada);
  // v2 no trae carpetas_totales/archivos_totales: solo se muestran si existen (compat v1).
  const carpTot = dc.carpetas_totales;
  const archTot = dc.archivos_totales;

  $body.innerHTML = `
    <div class="pdt-card">
      <h4>Docs .md del repo copiados a Nexo Center</h4>
      <div style="font-size:0.875rem;color:#8b949e">
        ${dc.ultima_auditoria ? `Auditoría a mano del ${esc(dc.ultima_auditoria)}` : 'Auditoría a mano sin fecha'}.
        No son los contratos de los nodos: esos van en «¿Cada pieza dice qué promete?».
      </div>
      <div style="display:flex;align-items:center;gap:12px;margin:10px 0 4px">
        <div style="flex:1">${pctBar(pct)}</div>
        <div style="font-size:1.25rem;font-weight:700;color:${pctColor};min-width:52px;text-align:right">${pct.toFixed(0)}%</div>
      </div>
      <div style="font-size:0.875rem;color:#8b949e">% de docs centralizados (docs en Center / docs .md del repo)</div>
    </div>

    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(170px,1fr));gap:10px">
      ${docMetricCard('&#128196; Docs .md del repo', String(mdN))}
      ${docMetricCard('&#128218; Docs en Nexo Center', String(centerN))}
      ${docMetricCard('&#9203; Pendientes por centralizar', pend == null ? '—' : String(pend),
                      (pend != null && pend > 0) ? '#d29922' : '#238636')}
      ${docMetricCard(alin.icon + ' Carpeta principal alineada', alin.label, alin.color)}
      ${carpTot != null ? docMetricCard('&#128193; Carpetas del repo', String(carpTot)) : ''}
      ${archTot != null ? docMetricCard('&#128462; Archivos totales', String(archTot)) : ''}
    </div>
    ${alin.kind === 'pending' ? `
      <div style="margin-top:8px;padding:8px 12px;font-size:0.875rem;color:#d29922;background:#d2992211;border:1px solid #d2992244;border-radius:6px">
        &#128993; Alineacion de carpeta principal <strong>pendiente</strong>: ${esc(alin.detail)}
      </div>` : ''}

    <p style="font-size:0.875rem;color:#6e7681;margin-top:10px;font-style:italic">
      Fuente: <code>status_metadata.doc_coverage</code> — mapeo de Argy-Q centralizado en Nexo Center. Read-only.
    </p>`;
}

function docMetricCard(labelHtml, value, valueColor) {
  return `
    <div class="pdt-card" style="margin-bottom:0">
      <div style="font-size:0.875rem;color:#8b949e">${labelHtml}</div>
      <div style="font-size:1.2rem;font-weight:700;color:${valueColor || '#c9d1d9'};margin-top:4px">${esc(String(value))}</div>
    </div>`;
}

function renderOperationalTab($body, ctx) {
  const { project } = ctx;
  const meta  = safeJsonParse(project.statusMetadata);
  const techs = safeArr(project.technologies);

  $body.innerHTML = `
    ${operacionalMedidoHtml(ctx)}
    <h4 style="margin:14px 0 6px;font-size:0.95rem;color:#c9d1d9">Anotado a mano</h4>
    <p style="font-size:0.875rem;color:#8b949e;margin:0 0 8px">Lo que alguien escribió en la ficha${project.updatedAt ? ` (ficha guardada el ${esc(formatearFecha(project.updatedAt))})` : ''}. Cada hito dice su fecha si la tiene y el veredicto de su dueño si ya lo revisó; quitarlo lo decide Josse.</p>
    <div class="pdt-op-toolbar" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;gap:8px;flex-wrap:wrap">
      <div style="font-size:0.875rem;color:#8b949e">Editable. Los cambios se persisten via <code>PUT /api/v1/Projects/by-key/${esc(project.projectKey)}</code>.</div>
      <div style="display:flex;gap:.5rem;align-items:center">
        <span id="pdt-op-status" style="font-size:0.875rem"></span>
        <button class="btn btn-primary btn-sm" id="pdt-op-save-btn" style="display:none">Guardar cambios</button>
      </div>
    </div>

    <div class="pdt-card">
      <h4>Estado operacional</h4>
      <div class="proj-fields-grid" style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:8px;margin-top:6px">
        <label class="proj-field">
          <span style="font-size:0.875rem;color:#8b949e">Version anotada a mano (la servida va arriba)</span>
          <input class="form-control form-control-sm" data-pdt-field="currentVersion"
                 value="${esc(project.currentVersion || '')}" placeholder="ej: 2.2.114">
        </label>
        <label class="proj-field">
          <span style="font-size:0.875rem;color:#8b949e">Rama activa</span>
          <input class="form-control form-control-sm" data-pdt-field="activeBranch"
                 value="${esc(project.activeBranch || project.repositoryBranch || '')}" placeholder="ej: main">
        </label>
        <label class="proj-field">
          <span style="font-size:0.875rem;color:#8b949e">URL de deploy</span>
          <input class="form-control form-control-sm" data-pdt-field="deployUrl"
                 value="${esc(project.deployUrl || '')}" placeholder="https://...">
        </label>
        <label class="proj-field">
          <span style="font-size:0.875rem;color:#8b949e">Ultimo deploy</span>
          <input class="form-control form-control-sm" type="datetime-local" data-pdt-field="lastDeployAt"
                 value="${project.lastDeployAt ? new Date(project.lastDeployAt).toISOString().slice(0,16) : ''}">
        </label>
        <label class="proj-field">
          <span style="font-size:0.875rem;color:#8b949e">Estado</span>
          <select class="form-control form-control-sm" data-pdt-field="status">
            ${['active','paused','deprecated'].map(s =>
              `<option value="${s}" ${project.status === s ? 'selected' : ''}>${s}</option>`
            ).join('')}
          </select>
        </label>
        <label class="proj-field">
          <span style="font-size:0.875rem;color:#8b949e">Estado operacional</span>
          <select class="form-control form-control-sm" data-pdt-field="operationalStatus">
            ${['operational','production','beta','alpha','maintenance','paused'].map(s =>
              `<option value="${s}" ${(project.operationalStatus || '') === s ? 'selected' : ''}>${s}</option>`
            ).join('')}
          </select>
        </label>
      </div>
      <p style="font-size:0.875rem;color:#6e7681;margin-top:6px;font-style:italic">
        Todo lo de esta ficha se guarda (PUT by-key: version, rama, URL, ultimo deploy, estados, descripcion, objetivos y lo anotado). Es lo escrito a mano: la version servida la dice la cabecera.
      </p>
    </div>

    <div class="pdt-card">
      <h4>Descripcion y objetivos</h4>
      <label class="proj-field" style="display:block;margin-bottom:8px">
        <span style="font-size:0.875rem;color:#8b949e">Descripcion</span>
        <textarea class="form-control form-control-sm" data-pdt-field="description" rows="2">${esc(project.description || '')}</textarea>
      </label>
      <label class="proj-field" style="display:block">
        <span style="font-size:0.875rem;color:#8b949e">Objetivos</span>
        <textarea class="form-control form-control-sm" data-pdt-field="goals" rows="2">${esc(project.goals || '')}</textarea>
      </label>
    </div>

    <div class="pdt-card">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <h4 style="margin:0">Cambios recientes</h4>
        <button class="btn btn-xs btn-ghost" data-pdt-add="change">+ agregar</button>
      </div>
      <ul class="pdt-list-edit" data-pdt-list="change" style="list-style:none;padding:0;margin:6px 0 0 0">
        ${itemsAnotados(meta.recentChanges).map(t => listInputItem(t, 'change', meta.hitos_revisados)).join('')}
      </ul>
    </div>

    <div class="pdt-card">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <h4 style="margin:0">Proximos pasos</h4>
        <button class="btn btn-xs btn-ghost" data-pdt-add="step">+ agregar</button>
      </div>
      <ul class="pdt-list-edit" data-pdt-list="step" style="list-style:none;padding:0;margin:6px 0 0 0">
        ${itemsAnotados(meta.nextSteps).map(t => listInputItem(t, 'step', meta.hitos_revisados)).join('')}
      </ul>
    </div>

    <div class="pdt-card">
      <div style="display:flex;justify-content:space-between;align-items:center">
        <h4 style="margin:0">Bloqueos</h4>
        <button class="btn btn-xs btn-ghost" data-pdt-add="blocker">+ agregar</button>
      </div>
      <ul class="pdt-list-edit" data-pdt-list="blocker" style="list-style:none;padding:0;margin:6px 0 0 0">
        ${itemsAnotados(meta.blockers).map(t => listInputItem(t, 'blocker', meta.hitos_revisados)).join('')}
      </ul>
    </div>

    <div class="pdt-card">
      <h4>Notas</h4>
      <textarea class="form-control form-control-sm" data-pdt-field="meta-notes" rows="3"
                placeholder="Notas libres de contexto...">${esc(meta.notes || '')}</textarea>
      ${veredictoDe(meta.notes, meta.hitos_revisados, { dentro: true }).map((r) => `<div class="pdt-veredicto" data-pdt-veredicto>${veredictoHtml(r)} «${esc(r.hito)}»</div>`).join('')}
    </div>

    <div class="pdt-card">
      <h4>Tecnologias <span style="font-size:0.875rem;font-weight:normal;color:#6e7681">(read-only)</span></h4>
      <div style="display:flex;flex-wrap:wrap;gap:.35rem;margin-top:4px">
        ${techs.length ? techs.map(t => `<span class="area-tag">${esc(t)}</span>`).join('') : '<span style="color:#6e7681;font-size:0.875rem">Sin tecnologias listadas</span>'}
      </div>
    </div>`;

  wireOperationalTab($body, project, meta);
}

// Un hito escrito a mano: texto editable, su fecha (o "sin fecha") y el veredicto de su dueno si ya lo reviso.
function listInputItem(item, type, revisados) {
  const { texto, fecha } = typeof item === 'object' && item ? item : { texto: item || '', fecha: null };
  const rev = texto ? veredictoDe(texto, revisados) : null;
  return `
    <li class="pdt-list-edit-item" data-pdt-type="${type}" style="margin-bottom:6px">
      <div style="display:flex;gap:6px">
        <input class="form-control form-control-sm pdt-list-input" value="${esc(texto)}" data-pdt-list-field="${type}" style="flex:1;min-width:0">
        <button class="btn btn-xs btn-ghost pdt-del-item" title="Eliminar" style="flex-shrink:0">✕</button>
      </div>
      ${texto ? `<div class="pdt-anotado-meta">${fecha ? esc(fecha) : 'sin fecha'}${rev ? ` · <span data-pdt-veredicto>${veredictoHtml(rev)}</span>` : ''}</div>` : ''}
    </li>`;
}

const COLOR_VEREDICTO = { VIGENTE: '#3fb950', CUMPLIDO: '#58a6ff', OBSOLETO: '#d29922' };
function veredictoHtml(r) {
  const v = String(r.veredicto || '').toUpperCase();
  const c = COLOR_VEREDICTO[v] || '#8b949e';
  return `<strong style="color:${c}">${esc(v || 'revisado')}</strong>${r.motivo ? `: ${esc(r.motivo)}` : ''}${r.por ? ` (${esc(r.por)}${r.fecha ? `, ${esc(r.fecha)}` : ''})` : ''}`;
}

// Lo medido de Operacional (Ares #39430): cambios, proximos pasos y bloqueos desde la bitacora y las tareas del dueno.
function operacionalMedidoHtml(ctx) {
  const { project, dueno, origenesDueno, eventosOperacional = [], tareasDelDueno = [], tareasTotal = 0 } = ctx;
  const repo = repoDe(project.repositoryUrl);
  const cambios = cambiosRecientes(eventosOperacional, { projectKey: project.projectKey, repo, origenesDueno });
  const bloqueos = bloqueosAbiertos(eventosOperacional, { origenesDueno });
  const pasos = proximosPasos(tareasDelDueno);
  const fecha = (f) => (f ? formatearFecha(f) : '');
  const quien = dueno ? `del dueño (${esc(dueno)})` : 'del dueño';
  const sinDueno = !dueno ? '<p class="pdt-op-vacio">Este repo todavía no tiene dueño en la foto de salud: no se pueden pedir sus tareas ni sus bloqueos.</p>' : '';
  return `
    <div class="pdt-op-medido" data-pdt-op-medido>
      <div class="pdt-card" data-pdt-op="cambios">
        <h4>Cambios recientes</h4>
        <div class="pdt-op-fuente">Los últimos [VIVO] y [MERGE] ${quien} y de quien nombre <code>${esc(project.projectKey)}</code>${repo ? ` o <code>${esc(repo)}</code>` : ''} (bitácora).</div>
        ${cambios.length ? `<ul class="pdt-op-lista">${cambios.map((c) => `<li><code>#${esc(c.id)}</code> ${esc(fecha(c.fecha))} · ${esc(c.titulo)}</li>`).join('')}</ul>`
          : '<p class="pdt-op-vacio">Sin [VIVO] ni [MERGE] en los últimos eventos que se leyeron.</p>'}
      </div>
      <div class="pdt-card" data-pdt-op="pasos">
        <h4>Próximos pasos</h4>
        <div class="pdt-op-fuente">Tareas abiertas ${quien}, con su misión y su edad (tareas, filtro por responsable).</div>
        ${sinDueno}
        ${pasos.vivas.length ? `<ul class="pdt-op-lista">${pasos.vivas.slice(0, 10).map((p) => `<li>${p.estado === 'in_progress' ? '<strong>en curso</strong>' : 'pendiente'} · ${esc(p.titulo)} <span class="pdt-op-dim">· ${esc(p.mision)}${p.edadDias !== null ? ` · ${p.edadDias} d` : ''}</span></li>`).join('')}</ul>`
          : (dueno ? '<p class="pdt-op-vacio">Sin tareas abiertas del dueño.</p>' : '')}
        ${pasos.vivas.length > 10 ? `<p class="pdt-op-dim">y ${pasos.vivas.length - 10} más.</p>` : ''}
        ${tareasTotal > tareasDelDueno.length ? `<p class="pdt-op-corte" data-pdt-op-corte>Se ven ${tareasDelDueno.length} de ${tareasTotal} tareas abiertas: la API entrega hasta ${TAREAS_POR_ESTADO} por estado.</p>` : ''}
        ${dueno ? '<p class="pdt-op-dim">Las tareas con dos responsables (A+P y similares) no salen aquí: el filtro es por responsable exacto.</p>' : ''}
        ${pasos.congeladas.length ? `<p class="pdt-op-dim">${pasos.congeladas.length} congeladas (sin movimiento; se descongelan si una tarea nueva las cita).</p>` : ''}
      </div>
      <div class="pdt-card" data-pdt-op="bloqueos">
        <h4>Bloqueos</h4>
        <div class="pdt-op-fuente">[BLOCKED] ${quien} de los últimos 7 días que ningún evento posterior responde (bitácora).</div>
        ${bloqueos.length ? `<ul class="pdt-op-lista">${bloqueos.map((b) => `<li><code>#${esc(b.id)}</code> ${esc(fecha(b.fecha))} · ${esc(b.titulo)}${b.necesita ? ` <span class="pdt-op-dim">· necesita: ${esc(b.necesita)}</span>` : ''}</li>`).join('')}</ul>`
          : '<p class="pdt-op-vacio">Sin bloqueos abiertos.</p>'}
      </div>
    </div>
    <style>
      .pdt-op-fuente { font-size:0.875rem;color:#6e7681;margin-bottom:6px; }
      .pdt-op-lista  { list-style:none;padding:0;margin:0;font-size:0.875rem;color:#c9d1d9; }
      .pdt-op-lista li { padding:4px 0;border-bottom:1px solid #21262d;overflow-wrap:anywhere; }
      .pdt-op-lista li:last-child { border-bottom:none; }
      .pdt-op-dim    { color:#8b949e;font-size:0.875rem; }
      .pdt-op-corte  { color:#d29922;font-size:0.875rem;margin:6px 0 0; }
      .pdt-op-vacio  { color:#6e7681;font-size:0.875rem;margin:0; }
      .pdt-anotado-meta { font-size:0.875rem;color:#6e7681;margin-top:2px;overflow-wrap:anywhere; }
      .pdt-veredicto { font-size:0.875rem;color:#8b949e;margin-top:6px;overflow-wrap:anywhere; }
    </style>`;
}

function wireOperationalTab($body, project, existingMeta) {
  const $saveBtn = $body.querySelector('#pdt-op-save-btn');
  const $status  = $body.querySelector('#pdt-op-status');
  let isDirty = false;

  function markDirty() {
    if (isDirty) return;
    isDirty = true;
    $saveBtn.style.display = 'inline-block';
    $status.textContent = 'cambios sin guardar';
    $status.style.color = '#d29922';
  }

  // dirty trigger: cualquier input/textarea/select dentro de la pestana
  $body.addEventListener('input', (e) => {
    if (e.target.matches('[data-pdt-field], [data-pdt-list-field]')) markDirty();
  });
  $body.addEventListener('change', (e) => {
    if (e.target.matches('[data-pdt-field]')) markDirty();
  });

  // botones + agregar de listas
  $body.addEventListener('click', (e) => {
    const addBtn = e.target.closest('[data-pdt-add]');
    if (addBtn) {
      const type = addBtn.dataset.pdtAdd;
      const ul = $body.querySelector(`[data-pdt-list="${type}"]`);
      if (!ul) return;
      ul.insertAdjacentHTML('beforeend', listInputItem('', type));
      const last = ul.querySelector('li:last-child input');
      last?.focus();
      markDirty();
      return;
    }
    const delBtn = e.target.closest('.pdt-del-item');
    if (delBtn) {
      delBtn.closest('li')?.remove();
      markDirty();
    }
  });

  // guardar
  $saveBtn.addEventListener('click', async () => {
    $saveBtn.disabled = true;
    $status.textContent = 'guardando…';
    $status.style.color = '#8b949e';

    const payload = buildOperationalPayload($body, existingMeta);
    const res = await ProjectApiClient.updateByKey(project.projectKey, payload);

    if (res.ok) {
      isDirty = false;
      $saveBtn.style.display = 'none';
      $saveBtn.disabled = false;
      $status.textContent = '✓ guardado';
      $status.style.color = '#3fb950';
      // Mergear el proyecto actualizado en el contexto local para futuras lecturas
      const updated = res.data?.data ?? res.data ?? {};
      Object.assign(project, updated);
      // Si la respuesta trae statusMetadata, actualizar la referencia de meta
      if (updated.statusMetadata) {
        Object.assign(existingMeta, safeJsonParse(updated.statusMetadata));
      }
    } else {
      $saveBtn.disabled = false;
      const isAuthErr = res.status === 401 || /401|unauthor/i.test(res.error || '');
      $status.textContent = isAuthErr
        ? '⚠ sesion expirada — vuelve a iniciar'
        : `error: ${res.error || res.status || 'desconocido'}`;
      $status.style.color = '#f85149';
    }
  });
}

function buildOperationalPayload($body, existingMeta) {
  const fld = name => $body.querySelector(`[data-pdt-field="${name}"]`)?.value?.trim() ?? null;
  const collect = type => [...$body.querySelectorAll(`[data-pdt-list-field="${type}"]`)].map(i => i.value);
  // Lo que ya existia conserva su forma y su fecha; lo nuevo se guarda con la de hoy (PROYECTOS-ALINEADOS-T1).
  const hoy = new Date().toISOString().slice(0, 10);

  const statusMetadata = {
    ...existingMeta,
    recentChanges: listaParaGuardar(collect('change'), existingMeta.recentChanges, hoy),
    nextSteps:     listaParaGuardar(collect('step'), existingMeta.nextSteps, hoy),
    blockers:      listaParaGuardar(collect('blocker'), existingMeta.blockers, hoy),
    notes:         fld('meta-notes') || null,
  };

  // La API guarda tambien version, rama, URL y ultimo deploy (ProjectService: current_version, active_branch,
  // deploy_url, last_deploy_at); antes el panel no los mandaba y la ficha decia que no persistian.
  // El campo muestra la hora UTC (toISOString().slice(0,16)): se lee como UTC para que guardar sin tocarlo no la corra.
  const deploy = fld('lastDeployAt') ? fld('lastDeployAt') + 'Z' : null;
  return {
    description:       fld('description') || null,
    goals:             fld('goals') || null,
    status:            fld('status') || null,
    operationalStatus: fld('operationalStatus') || null,
    currentVersion:    fld('currentVersion') || null,
    activeBranch:      fld('activeBranch') || null,
    deployUrl:         fld('deployUrl') || null,
    lastDeployAt:      deploy ? new Date(deploy).toISOString() : null,
    statusMetadata,
  };
}
